import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { filtersToParams } from '@/components/dashboard/DashboardFilters';

/**
 * Hook reusable para cargar datos de los endpoints /dashboard/v2/*.
 *
 * Importante: los params se serializan con JSON.stringify como identidad
 * estable. Esto evita el problema clásico de "useEffect en loop infinito"
 * cuando el caller pasa objetos inline (`filters={...}`, `extra={limit: 5}`)
 * que cambian de referencia en cada render pero contenido idéntico.
 *
 * Uso:
 *   const { data, loading, error, reload } = useDashboardV2({
 *     endpoint: 'kpis',
 *     filters,
 *     vertical: 'real_estate',
 *     extra: { months_back: 12 },
 *   });
 */
export default function useDashboardV2({ endpoint, filters, vertical, extra }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Construye params en cada render (barato; es solo un object literal),
  // pero usa el JSON serializado como dependency del useEffect — la
  // identidad estable elimina el loop.
  const params = { ...filtersToParams(filters || {}) };
  if (vertical) params.vertical = vertical;
  if (extra && typeof extra === 'object') Object.assign(params, extra);
  const paramsKey = JSON.stringify(params);

  // Mantenemos una ref con la función de reload para exponerla sin
  // depender de las dependencias del useEffect.
  const reloadRef = useRef(() => {});
  reloadRef.current = () => {
    setLoading(true);
    setError(null);
    api.get(`/dashboard/v2/${endpoint}`, { params })
      .then(r => setData(r.data))
      .catch(e => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Error al cargar dashboard');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey]);

  return { data, loading, error, reload: () => reloadRef.current() };
}
